import { Injectable } from '@angular/core';
import PhotoSwipe from 'photoswipe';

export interface LightboxPhoto {
  src: string; // data URL or blob URL
  width: number;
  height: number;
  alt?: string;
}

const HELP_HTML = `
  <div class="pswp-help-panel">
    <button class="pswp-help-close" type="button" aria-label="Close help">&#x2715;</button>
    <h3 class="pswp-help-title">Photo Viewer</h3>
    <ul class="pswp-help-list">
      <li><strong>Navigate</strong> — Tap ‹ › arrows or swipe left / right</li>
      <li><strong>Zoom in</strong> — Pinch open or double-tap</li>
      <li><strong>Zoom out</strong> — Pinch close or double-tap again</li>
      <li><strong>Close</strong> — Tap × or swipe down</li>
    </ul>
    <p class="pswp-help-desktop">
      On desktop: ← → keys to navigate &nbsp;·&nbsp; scroll to zoom &nbsp;·&nbsp; Esc to close
    </p>
    <p class="pswp-help-dismiss">Tap anywhere to dismiss</p>
  </div>
`;

/**
 * Service for opening PhotoSwipe v5 lightbox galleries.
 * Handles single and multi-photo use cases with proper dimension resolution.
 */
@Injectable({ providedIn: 'root' })
export class PhotoLightboxService {
  /**
   * Open PhotoSwipe lightbox for one or more photos.
   * @param photos Array of photo descriptors with dimensions
   * @param index  Which photo to open first (default 0)
   */
  open(photos: LightboxPhoto[], index = 0): void {
    const gallery = new PhotoSwipe({
      dataSource: photos,
      index,
      loop: photos.length > 1,
      closeOnVerticalDrag: true,
      wheelToZoom: true,
      indexIndicatorSep: ' of ',
    });

    // Register help button in the top bar (between zoom:10 and close:20)
    gallery.on('uiRegister', () => {
      gallery.ui!.registerElement({
        name: 'help-button',
        order: 15,
        isButton: true,
        appendTo: 'bar',
        html: '<span class="pswp-help-icon" aria-hidden="true">?</span>',
        title: 'Photo viewer help',
        ariaLabel: 'Photo viewer help',
        onClick: (_e, _el, pswp) => {
          const root = pswp.element!;
          const existing = root.querySelector('.pswp-help-overlay') as HTMLElement | null;
          if (existing) {
            existing.remove();
            return;
          }
          const overlay = document.createElement('div');
          overlay.className = 'pswp-help-overlay';
          overlay.innerHTML = HELP_HTML;
          // close on backdrop click (but not on panel click)
          overlay.addEventListener('click', (e) => {
            if (e.target === overlay) overlay.remove();
          });
          // close button inside panel
          overlay
            .querySelector('.pswp-help-close')
            ?.addEventListener('click', () => overlay.remove());
          root.appendChild(overlay);
        },
      });
    });

    gallery.init();
  }

  /**
   * Open a single photo, resolving its natural dimensions first.
   * Falls back to 1920×1080 if the image hasn't loaded.
   */
  openSingle(src: string, alt?: string): void {
    const img = new Image();
    img.onload = () =>
      this.open([{ src, width: img.naturalWidth, height: img.naturalHeight, alt }]);
    img.onerror = () => this.open([{ src, width: 1920, height: 1080, alt }]);
    img.src = src;
  }

  /**
   * Open a gallery of photos, resolving all dimensions before showing.
   * Uses Promise.allSettled so a single failed load won't block the gallery.
   */
  openMultiple(srcs: string[], startIndex = 0): void {
    const promises = srcs.map(
      (src) =>
        new Promise<LightboxPhoto>((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ src, width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => resolve({ src, width: 1920, height: 1080 });
          img.src = src;
        }),
    );

    Promise.allSettled(promises).then((results) => {
      const photos: LightboxPhoto[] = results
        .filter((r): r is PromiseFulfilledResult<LightboxPhoto> => r.status === 'fulfilled')
        .map((r) => r.value);

      if (photos.length > 0) {
        this.open(photos, startIndex);
      }
    });
  }
}
